import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Flame, Search, Loader2, Building2, ArrowUpDown,
  CheckCircle, AlertCircle, Clock, AlertTriangle, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE_STYLES = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  orange: 'bg-orange-100 text-orange-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
};

const STATUS_ICONS = {
  green: <CheckCircle className="w-3.5 h-3.5" />,
  yellow: <Clock className="w-3.5 h-3.5" />,
  orange: <AlertTriangle className="w-3.5 h-3.5" />,
  red: <AlertCircle className="w-3.5 h-3.5" />,
  gray: <Clock className="w-3.5 h-3.5" />,
};

const PROCESSES = ['SMAW', 'FCAW', 'GMAW', 'GTAW'];

// Format YYYY-MM-DD (or ISO) as MM-DD-YYYY without timezone shift.
const formatMMDDYYYY = (value) => {
  if (!value) return '—';
  const dateOnly = String(value).split('T')[0];
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return dateOnly;
  return `${m[2]}-${m[3]}-${m[1]}`;
};

function StatusBadge({ color, daysRemaining }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_STYLES[color] || STATUS_BADGE_STYLES.gray}`}
      data-testid="welder-status-badge"
    >
      {STATUS_ICONS[color] || STATUS_ICONS.gray}
      {daysRemaining !== null && daysRemaining !== undefined ? `${daysRemaining}d` : 'N/A'}
    </span>
  );
}

export default function WeldingPersonnelPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [welders, setWelders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('all');
  const [processFilter, setProcessFilter] = useState('all');
  const [sortKey, setSortKey] = useState('current_due_date'); // 'technician_name' | 'current_due_date'
  const [sortDir, setSortDir] = useState('asc');

  const isTech = user?.role === 'TECHNICIAN';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [welderRes, facRes] = await Promise.all([
        api.get('/lms/welding-personnel'),
        api.get('/lms/facilities'),
      ]);
      setWelders(Array.isArray(welderRes.data) ? welderRes.data : []);
      setFacilities(Array.isArray(facRes.data) ? facRes.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load welding personnel');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isTech) fetchData();
  }, [fetchData, isTech]);

  const filteredAndSorted = useMemo(() => {
    let rows = welders;
    if (facilityFilter !== 'all') {
      rows = rows.filter((w) => w.facility_id === facilityFilter);
    }
    if (processFilter !== 'all') {
      rows = rows.filter((w) => (w.processes || []).includes(processFilter));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter((w) =>
        (w.technician_name || '').toLowerCase().includes(term) ||
        (w.technician_employee_id || '').toLowerCase().includes(term) ||
        (w.facility_name || '').toLowerCase().includes(term) ||
        (w.process_display || '').toLowerCase().includes(term)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      const av = a[sortKey] || '';
      const bv = b[sortKey] || '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [welders, facilityFilter, processFilter, searchTerm, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (isTech) {
    return (
      <div className="p-8" data-testid="welding-personnel-forbidden">
        <h1 className="text-2xl font-semibold text-gray-800">Access Denied</h1>
        <p className="text-gray-600 mt-2">Welding Personnel is not available for Technicians.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="welding-personnel-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
          <Flame className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-[#323033]">Welding Personnel</h1>
          <p className="text-sm text-[#5A5A5A]">
            Technicians with active welding qualifications.
          </p>
        </div>
      </div>

      {/* Phase 3 — Auto Log "verify accuracy" notice. Visible to Managers,
          Operators, and Admins (anyone with access to this page) so they know
          which facilities are auto-logging. Read-only here; only Admins can
          toggle the setting (Facility Management → facility detail). */}
      {facilities.filter((f) => f.welding_auto_log_enabled).length > 0 && (
        <div
          className="border border-emerald-200 bg-emerald-50/60 rounded-md p-3 space-y-1"
          data-testid="welding-auto-log-notice"
        >
          <div className="flex items-center gap-2 text-emerald-800 text-sm font-medium">
            <Zap className="w-4 h-4" />
            Welder Maintenance Auto Log is ON for the following facilities — Verify Accuracy.
          </div>
          <ul className="ml-6 list-disc text-sm text-emerald-900/80">
            {facilities
              .filter((f) => f.welding_auto_log_enabled)
              .map((f) => (
                <li key={f.id} data-testid={`auto-log-facility-${f.id}`}>{f.name}</li>
              ))}
          </ul>
        </div>
      )}

      {/* Filters card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, Tech ID, facility…"
                  className="pl-8"
                  data-testid="welder-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Facility</Label>
              <select
                value={facilityFilter}
                onChange={(e) => setFacilityFilter(e.target.value)}
                className="w-full mt-1 h-9 px-3 border rounded-md bg-background text-sm"
                data-testid="welder-facility-filter"
              >
                <option value="all">All facilities</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Process</Label>
              <select
                value={processFilter}
                onChange={(e) => setProcessFilter(e.target.value)}
                className="w-full mt-1 h-9 px-3 border rounded-md bg-background text-sm"
                data-testid="welder-process-filter"
              >
                <option value="all">All processes</option>
                {PROCESSES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <p className="text-xs text-gray-500" data-testid="welder-count">
                {filteredAndSorted.length} welder{filteredAndSorted.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredAndSorted.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500" data-testid="welder-empty-state">
              No welding personnel match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3">
                      <button
                        onClick={() => toggleSort('technician_name')}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        data-testid="sort-name"
                      >
                        Name <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="px-4 py-3">Tech ID</th>
                    <th className="px-4 py-3">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Facility
                      </span>
                    </th>
                    <th className="px-4 py-3">Process</th>
                    <th className="px-4 py-3">
                      <button
                        onClick={() => toggleSort('current_due_date')}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        data-testid="sort-due"
                      >
                        Due Date <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAndSorted.map((w) => (
                    <tr
                      key={w.technician_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/training/welding-personnel/${w.technician_id}`)}
                      data-testid={`welder-row-${w.technician_id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{w.technician_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{w.technician_employee_id || '—'}</td>
                      <td className="px-4 py-3 text-sm">{w.facility_name || '—'}</td>
                      <td className="px-4 py-3 text-sm" data-testid={`welder-processes-${w.technician_id}`}>
                        {/* 2026-07-14 — AAR: show ONE process badge = the process driving the earliest due date. */}
                        {w.driving_process
                          ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200 text-xs font-medium">
                              {w.driving_process}
                            </span>
                          )
                          : (w.process_display || '—')}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {w.due_source === 'unset' || !w.current_due_date
                          ? <span className="text-gray-400">Not Set</span>
                          : formatMMDDYYYY(w.current_due_date)}
                      </td>
                      <td className="px-4 py-3">
                        {w.due_source === 'unset'
                          ? <span className="text-xs text-gray-400" data-testid={`welder-status-unset-${w.technician_id}`}>Not Set</span>
                          : <StatusBadge color={w.status_color} daysRemaining={w.days_remaining} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
